import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { physicsWorld } from '../Physics/PhysicsWorld';
import store from '../../redux/store';
import {
  buildColliderVisualFromShape,
  createColliderMaterial,
  findSceneMesh,
  getColliderViewSignature,
  resolveCenterOfMassOffset,
} from '../Physics/colliderVisualUtils';

/** Latest design-mode collider overlay (for live type/view updates). */
let activeDesignColliderRenderer = null;

export function getDesignColliderRenderer() {
  return activeDesignColliderRenderer;
}

/**
 * Design-mode collider overlay for physics objects (hidden in preview).
 */
export default class CannonDebugRenderer {
  constructor(scene) {
    this.scene = scene;
    this.material = createColliderMaterial();
    this.visuals = new Map();
    activeDesignColliderRenderer = this;
  }

  clearVisuals() {
    this.visuals.forEach((visual) => {
      try {
        this.scene.remove(visual);
      } catch (e) {}
    });
    this.visuals.clear();
  }

  remove(objectId) {
    if (!objectId) return;
    const visual = this.visuals.get(objectId);
    if (!visual) return;
    try {
      this.scene.remove(visual);
    } catch (e) {}
    this.visuals.delete(objectId);
  }

  /** Drop cached overlay so the next update() rebuilds with current Redux props. */
  invalidate(objectId) {
    this.remove(objectId);
  }

  buildDesignColliderVisual(mesh, physicsProperties) {
    const shape = physicsWorld.createShape(
      mesh,
      physicsProperties?.colliderType || 'Box'
    );
    if (!shape) return null;

    const group = new THREE.Group();
    const offset = resolveCenterOfMassOffset(physicsProperties, mesh);
    const offsetVec = offset
      ? new CANNON.Vec3(offset.x || 0, offset.y || 0, offset.z || 0)
      : null;

    group.add(buildColliderVisualFromShape(shape, this.material, offsetVec));

    const worldPos = new THREE.Vector3();
    const worldQuat = new THREE.Quaternion();
    mesh.getWorldPosition(worldPos);
    mesh.getWorldQuaternion(worldQuat);
    group.position.copy(worldPos);
    group.quaternion.copy(worldQuat);

    group.name = `${mesh.name || mesh.uuid}-collider-view`;
    group.__colliderSignature = getColliderViewSignature(
      physicsProperties,
      mesh
    );
    return group;
  }

  syncDesignColliderVisual(objectId, mesh, physicsProperties) {
    const signature = getColliderViewSignature(physicsProperties, mesh);
    const existing = this.visuals.get(objectId);

    if (existing && existing.__colliderSignature === signature) {
      const worldPos = new THREE.Vector3();
      const worldQuat = new THREE.Quaternion();
      mesh.getWorldPosition(worldPos);
      mesh.getWorldQuaternion(worldQuat);
      existing.position.copy(worldPos);
      existing.quaternion.copy(worldQuat);
      return;
    }

    if (existing) {
      this.remove(objectId);
    }

    const visual = this.buildDesignColliderVisual(mesh, physicsProperties);
    if (!visual) return;

    this.scene.add(visual);
    this.visuals.set(objectId, visual);
  }

  updateDesignColliders() {
    const physicsObjects = store.getState().physics?.physicsObjects || [];
    const activeIds = new Set();

    physicsObjects.forEach((physicsObject) => {
      if (physicsObject.isViewCollider === false) return;

      const objectId = physicsObject.objectId;
      if (!objectId) return;

      const mesh = findSceneMesh(this.scene, objectId);
      if (!mesh) return;

      activeIds.add(objectId);
      this.syncDesignColliderVisual(objectId, mesh, physicsObject);
    });

    this.visuals.forEach((_, objectId) => {
      if (!activeIds.has(objectId)) {
        this.remove(objectId);
      }
    });
  }

  update() {
    const windowType = store.getState()?.window?.window_type;
    if (windowType === 'preview') {
      this.clearVisuals();
      return;
    }

    this.updateDesignColliders();
  }

  dispose() {
    this.clearVisuals();
    if (activeDesignColliderRenderer === this) {
      activeDesignColliderRenderer = null;
    }
  }
}
